import i18next from 'i18next';
import HttpBackend from 'i18next-http-backend';
import ICU from 'i18next-icu';
import { initReactI18next, useTranslation as useReactI18nextTranslation } from 'react-i18next';
import { LocaleKeys } from './LocaleKeys';
import { DEFAULT_LOCALE, readLocaleCookie, SUPPORTED_LOCALES } from './locales';
const initialLanguage = readLocaleCookie(document.cookie);
if (process.env.NODE_ENV === 'development') {
    // Development and test mode: Load the active language synchronously (no HTTP, no Suspense needed).
    // Always also load the fallback locale so missing keys fall back to English instead of
    // rendering the literal key path.
    const activeTranslations = require("../../locales/".concat(initialLanguage, ".json"));
    const resources = initialLanguage === DEFAULT_LOCALE ? {
        [DEFAULT_LOCALE]: {
            translation: activeTranslations
        }
    } : {
        [initialLanguage]: {
            translation: activeTranslations
        },
        [DEFAULT_LOCALE]: {
            translation: require("../../locales/".concat(DEFAULT_LOCALE, ".json"))
        }
    };
    i18next.use(ICU).use(initReactI18next).init({
        lng: initialLanguage,
        fallbackLng: DEFAULT_LOCALE,
        supportedLngs: [
            ...SUPPORTED_LOCALES
        ],
        resources,
        initImmediate: false,
        interpolation: {
            escapeValue: false
        },
        react: {
            useSuspense: false
        }
    });
} else {
    // Production: Lazy-load translations via HTTP
    // App is wrapped in Suspense to wait for translations before rendering
    i18next.use(HttpBackend).use(ICU).use(initReactI18next).init({
        lng: initialLanguage,
        fallbackLng: DEFAULT_LOCALE,
        supportedLngs: [
            ...SUPPORTED_LOCALES
        ],
        backend: {
            loadPath: "/locales/{{lng}}.json?v=".concat(GIT_SHA1)
        },
        interpolation: {
            escapeValue: false
        },
        // Enable Suspense - app waits for translations before rendering
        react: {
            useSuspense: true
        }
    });
}
/**
 * Hook for use in React components. Provides Suspense integration AND typed translations.
 *
 * This is the recommended way to use translations in React components:
 * - Integrates with React Suspense (waits for translations to load)
 * - Provides full type safety for translation keys and ICU arguments
 *
 * @example
 * import { useLocaleKeys } from '~/i18n';
 *
 * function MyComponent() {
 *     const { t } = useLocaleKeys();
 *     return <div>{t.errors.unexpected()}</div>;
 * }
 *
 * // With a sub-namespace path:
 * function SetupWidget() {
 *     const { t } = useLocaleKeys('dashboardPage.setupWidget');
 *     return <div>{t.uploadLogo.title()}</div>;
 * }
 */ export function useLocaleKeys(path) {
    const { i18n, ready } = useReactI18nextTranslation();
    // Both LocaleKeys and i18n.t are cast to `any` to avoid TS2589 (type instantiation too deep).
    // ILocaleKeys is a large generated type and i18n.t is deeply typed against en.json via
    // CustomTypeOptions — TypeScript exceeds its instantiation depth limit when resolving either.
    // The `as unknown as ILocaleKeys` cast on the result still enforces the return type so real
    // type errors on callers are caught.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tFn = i18n.t.bind(i18n);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fullT = LocaleKeys(tFn);
    const getNestedValue = (obj, pathStr)=>{
        return pathStr.split('.').reduce((acc, key)=>acc === null || acc === void 0 ? void 0 : acc[key], obj);
    };
    return {
        t: path ? getNestedValue(fullT, path) : fullT,
        i18n,
        ready
    };
}
export default i18next;
